import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { GoogleMapsService } from 'src/app/services/google-maps.service';
import { ParticularOrderService } from 'src/generated_client/api/particular-order.service';
import { CreateParticularOrderDto } from 'src/generated_client/model/createParticularOrderDto';
import { UpdateParticularOrderDto } from 'src/generated_client/model/updateParticularOrderDto';
import { ParticularOrderStatus } from 'src/generated_client/model/particularOrderStatus';
import { PaymentLocation } from 'src/generated_client/model/paymentLocation';
import { CalculateDeliveryCostRequest } from 'src/generated_client/model/calculateDeliveryCostRequest';
import { ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm-dialog.component';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { WaypointStatus } from 'src/generated_client/model/waypointStatus';
import { DeliveryPricingRulesService } from 'src/generated_client/api/deliveryPricingRules.service';
import { DeliveryPricingRuleDto } from 'src/generated_client/model/deliveryPricingRuleDto';
import { Category } from 'src/generated_client/model/category';
import { environment } from 'src/environments/environment';
import {
  DELIVERY_TIME_RANGE_PLACEHOLDER,
  HOUR_SUFFIX,
  PICKUP_TIME_RANGE_PLACEHOLDER,
} from 'src/app/shared/constants/time-text.constants';

@Component({
  selector: 'app-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.css'],
})
export class AddressFormComponent implements OnInit {
  readonly hourSuffix = HOUR_SUFFIX;
  readonly pickupTimeRangePlaceholder = PICKUP_TIME_RANGE_PLACEHOLDER;
  readonly deliveryTimeRangePlaceholder = DELIVERY_TIME_RANGE_PLACEHOLDER;
  originalPersonName = '';
  lastClientSearch = '';
  private clientSearchSeq = 0;
  private clientSearchTimer: any = null;

  private showErrorDialog(title: string, message: string): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: title,
        message: message,
        showCancelButton: false,
        confirmButtonText: 'Ok',
      },
    });
  }

  onCancel(): void {
    this.router.navigate(['/particular-orders-list']);
  }

  // Helper method to check if addresses can be edited based on order status
  canEditAddresses(): boolean {
    if (!this.isEditMode || this.orderStatus === null) {
      return true; // New orders or when status is not loaded yet
    }
    // Only allow editing if status is Nuevo (0) or Confirmado (1)
    return this.orderStatus === ParticularOrderStatus.Nuevo || this.orderStatus === ParticularOrderStatus.Confirmado;
  }

  // Helper method to check if shipment can be finalized based on order status
  canFinalizeShipment(): boolean {
    if (!this.isEditMode || this.orderStatus === null) {
      return false; // New orders or when status is not loaded yet cannot be finalized
    }
    // Only allow finalizing if status is Aceptado (3) or EnCurso (5)
    return this.orderStatus === ParticularOrderStatus.Aceptado || this.orderStatus === ParticularOrderStatus.EnCurso;
  }
  isEditMode = false;
  orderId: number | null = null;
  showDatePicker = false;
  orderStatus: ParticularOrderStatus | null = null;
  minDate: Date = (() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow;
  })(); 

  @Input() label: string = '';
  @Output() addressChange = new EventEmitter<any>();
  @Output() waypointsChange = new EventEmitter<any[]>();

  addressForm: FormGroup;
  distance: string | null = null;
  loadingDistance = false;
  loadingCost = false;
  @Input() waypoints: any[] = [];
  @Input() isEditing = false;
  totalDistanceKm: number = 0;

  pickupLat: number | null = null;
  pickupLng: number | null = null;
  deliveryLat: number | null = null;
  deliveryLng: number | null = null;
  distanceKm: number | null = null;

  loading = false;
  
  // Pricing rules
  pricingRules: DeliveryPricingRuleDto[] = [];
  selectedPricingRuleIds: number[] = [];
  
  // Pricing details from backend
  particularOrderPricingDetail: { deliveryPricingRuleId: number; value: number }[] = [];

  // Cached IVA percentage to keep calculations aligned with the selected rule in edit mode.
  private ivaPercentage: number | null = null;
  
  // Original waypoints loaded from backend (with IDs)
  originalWaypoints: any[] = [];

  // Client catalog
  clients: any[] = [];
  filteredClients: any[] = [];
  selectedClientId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private particularOrderService: ParticularOrderService,
    private googleMapsService: GoogleMapsService,
    private dialog: MatDialog,
    private deliveryPricingRulesService: DeliveryPricingRulesService,
    private http: HttpClient,
    private snackBar: SnackBarService
  ) {
    this.addressForm = this.fb.group({
      pickupAddress: ['', Validators.required],
      pickupAddressDetail: [''],
      deliveryAddress: ['', Validators.required],
      deliveryAddressDetail: [''],
      waypoints: this.fb.array([]),
      personName: ['', Validators.required],
      pickupTimeRange: ['', Validators.required],
      deliveryTimeRange: ['', Validators.required],
      pickupPhone: [
        '',
        [Validators.pattern(/^\d{10,15}$/)]
      ],
      deliveryPhone: [
        '',
        [Validators.pattern(/^\d{10,15}$/)]
      ],
      observations: [''],
      paymentLocation: ['Destino', Validators.required],
      costo: [
        null,
        [
          Validators.required,
          Validators.min(0),
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
        ],
      ],
      finalCost: [
        null,
        [
          Validators.min(0),
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
        ],
      ],
      finalCostWithIVA: [
        null,
        [
          Validators.min(0),
          Validators.pattern(/^\d+(\.\d{1,2})?$/),
        ],
      ],
      options: this.fb.group({
        idaVuelta: [false],
        objetoFueraCaja: [false],
        productos: [false],
        lluvia: [false],
        isScheduled: [false],
        scheduledAt: [null],
        feriadoComun: [false],
        feriadoEspecial: [false],
        tiempoEspera: [false],
        recargoFueraDeCiudad: [false],
        iva: [false],
      }),
      costoProductos: [null, [Validators.min(0)]],
      tiempoEsperaMinutos: [null, [Validators.min(1), Validators.pattern(/^[1-9]\d*$/)]],
    });

    this.addressForm
      .get('options.productos')
      ?.valueChanges.subscribe((checked) => {
        const costoProductosControl = this.addressForm.get('costoProductos');
        if (checked) {
          costoProductosControl?.setValidators([
            Validators.required,
            Validators.min(0),
          ]);
        } else {
          costoProductosControl?.clearValidators();
          costoProductosControl?.setValue(null, { emitEvent: false });
        };
        costoProductosControl?.updateValueAndValidity();
      });

    // Listen for isScheduled checkbox changes
    this.addressForm
      .get('options.isScheduled')
      ?.valueChanges.subscribe((checked) => {
        const scheduledAtControl = this.addressForm.get('options.scheduledAt');
        if (checked) {
          scheduledAtControl?.setValidators([Validators.required]);
          this.showDatePicker = true;
        } else {
          scheduledAtControl?.clearValidators();
          scheduledAtControl?.setValue(null, { emitEvent: false });
          this.showDatePicker = false;
        }
        scheduledAtControl?.updateValueAndValidity();
      });

    // Listen for tiempoEspera checkbox changes
    this.addressForm
      .get('options.tiempoEspera')
      ?.valueChanges.subscribe((checked) => {
        const tiempoEsperaMinutosControl = this.addressForm.get('tiempoEsperaMinutos');
        if (checked) {
          tiempoEsperaMinutosControl?.setValidators([
            Validators.required,
            Validators.min(1),
            Validators.pattern(/^[1-9]\d*$/)
          ]);
        } else {
          tiempoEsperaMinutosControl?.clearValidators();
          tiempoEsperaMinutosControl?.setValue(null, { emitEvent: false });
        }
        tiempoEsperaMinutosControl?.updateValueAndValidity();
      });

  
    this.addressForm
      .get('options.feriadoComun')
      ?.valueChanges.subscribe((checked) => {
        if (checked) {
          this.addressForm.get('options.feriadoEspecial')?.setValue(false, { emitEvent: false });
        }
      });

    this.addressForm
      .get('options.feriadoEspecial')
      ?.valueChanges.subscribe((checked) => {
        if (checked) {
          this.addressForm.get('options.feriadoComun')?.setValue(false, { emitEvent: false });
        }
      });

    // Listen for finalCost changes to calculate finalCostWithIVA
    this.addressForm
      .get('finalCost')
      ?.valueChanges.subscribe((finalCostValue) => {
        this.calculateFinalCostWithIVA(finalCostValue);
      });

    // Listen for IVA checkbox changes to recalculate finalCostWithIVA
    this.addressForm
      .get('options.iva')
      ?.valueChanges.subscribe(() => {
        const finalCostValue = this.addressForm.get('finalCost')?.value;
        this.calculateFinalCostWithIVA(finalCostValue);
      });
  }

  loadClients(): void {
    this.searchClients('');
  }

  searchClients(term: string): void {
    const seq = ++this.clientSearchSeq;
    const search = (term || '').trim();
    const url = `${environment.apiBaseUrl}/api/Client/GetAllByServicePaged?serviceName=Mensajeria&pageSize=20&search=${encodeURIComponent(search)}`;
    this.http.get<any>(url).subscribe({
      next: (result) => {
        if (seq !== this.clientSearchSeq) {
          return;
        }
        this.clients = (result && result.data) || [];
        this.filteredClients = [...this.clients];
        this.lastClientSearch = search.toLowerCase();
      },
      error: (err) => {
        if (seq !== this.clientSearchSeq) {
          return;
        }
        console.error('Error loading clients', err);
        this.clients = [];
        this.filteredClients = [];
        this.snackBar.openSnackBar('No se pudieron cargar los clientes.', 'Aceptar');
      }
    });
  }

  filterClients(event: any): void {
    const term = (event.target.value || '').trim();
    if (this.clientSearchTimer) {
      clearTimeout(this.clientSearchTimer);
    }
    this.clientSearchTimer = setTimeout(() => {
      this.clientSearchTimer = null;
      this.searchClients(term);
    }, 300);
  }

  onClientSelected(client: any): void {
    this.selectedClientId = client.cdClient;
    this.addressForm.get('personName')?.setValue(client.dsNombreFantasia || client.dsRazonSocial);
  }

  onClientInput(): void {
    const value = (this.addressForm.get('personName')?.value || '').trim();
    if (value === this.originalPersonName) {
      return;
    }
    if (!value) {
      this.selectedClientId = null;
      return;
    }
    const lower = value.toLowerCase();
    const match = this.clients.find(c =>
      (c.dsNombreFantasia || '').toLowerCase() === lower ||
      (c.dsRazonSocial || '').toLowerCase() === lower
    );
    if (match) {
      this.selectedClientId = match.cdClient;
    } else if (lower === this.lastClientSearch) {
      this.selectedClientId = null;
    }
  }

  displayClientName(client: any): string {
    return client ? (client.dsNombreFantasia || client.dsRazonSocial || '') : '';
  }

  get optionsFormGroup(): FormGroup {
    return this.addressForm.get('options') as FormGroup;
  }

  get costoProductosControl(): FormControl {
    return this.addressForm.get('costoProductos') as FormControl;
  }

  get tiempoEsperaMinutosControl(): FormControl {
    return this.addressForm.get('tiempoEsperaMinutos') as FormControl;
  }

  get costoControl(): FormControl {
    return this.addressForm.get('costo') as FormControl;
  }

  get finalCostControl(): FormControl {
    return this.addressForm.get('finalCost') as FormControl;
  }

  get finalCostWithIVAControl(): FormControl {
    return this.addressForm.get('finalCostWithIVA') as FormControl;
  }

  onScheduledChange(event: any): void {
    this.showDatePicker = event.checked;
  }

  get waypointsArray(): FormArray {
    return this.addressForm.get('waypoints') as FormArray;
  }

  get isScheduledControl(): FormControl {
    return this.addressForm.get('options.isScheduled') as FormControl;
  }

  get scheduledAtControl(): FormControl {
    return this.addressForm.get('options.scheduledAt') as FormControl;
  }

  // Calculate final cost with IVA based on the pricing rule
  calculateFinalCostWithIVA(finalCostValue: any): void {
    if (!finalCostValue || isNaN(parseFloat(finalCostValue))) {
      this.finalCostWithIVAControl.setValue(null, { emitEvent: false });
      return;
    }

    const finalCost = parseFloat(finalCostValue);
    const ivaChecked = this.addressForm.get('options.iva')?.value;

    // Only calculate IVA if the checkbox is checked
    if (!ivaChecked) {
      this.finalCostWithIVAControl.setValue(null, { emitEvent: false });
      return;
    }

    const ivaPercentage = this.getIvaPercentage();

    if (ivaPercentage > 0) {
      // Calculate IVA: finalCost * (ivaPercentage / 100)
      const ivaAmount = finalCost * (ivaPercentage / 100);
      const finalCostWithIVA = finalCost + ivaAmount;
      this.finalCostWithIVAControl.setValue(finalCostWithIVA.toFixed(2), { emitEvent: false });
    } else {
      // If no IVA rule exists, just copy the final cost
      this.finalCostWithIVAControl.setValue(finalCost.toFixed(2), { emitEvent: false });
    }
  }

  // Detalles del desglose de costos
  costBreakdown: {
    baseCost: number;
    extraKmsCost: number;
    returnTripCost: number;
    roundTripDiscount: number;
    productFee: number;
    oversizeFee: number;
    rainSurcharge: number;
    feriadoComunSurcharge: number;
    feriadoEspecialSurcharge: number;
    waitFee: number;
    recargoFueraDeCiudadSurcharge: number;
    ivaAmount: number;
    totalCost: number;
    finalTotalCost: number;
  } | null = null;

  private calculateBreakdownSubtotal(
    baseCost: number,
    extraKmsCost: number,
    returnTripCost: number,
    roundTripDiscount: number,
    productFee: number,
    oversizeFee: number,
    rainSurcharge: number,
    feriadoComunSurcharge: number,
    feriadoEspecialSurcharge: number,
    waitFee: number,
    recargoFueraDeCiudadSurcharge: number
  ): number {
    const roundTripNet = roundTripDiscount > 0 ? (returnTripCost - roundTripDiscount) : 0;

    return baseCost
      + extraKmsCost
      + roundTripNet
      + productFee
      + oversizeFee
      + rainSurcharge
      + feriadoComunSurcharge
      + feriadoEspecialSurcharge
      + waitFee
        + recargoFueraDeCiudadSurcharge;
  }

  calculateCost() {
    if (this.loadingCost || this.totalDistanceKm === 0) return;

    this.loadingCost = true;

    const options = this.addressForm.get('options')?.value;
    const request: CalculateDeliveryCostRequest = {
      totalDistanceKm: this.totalDistanceKm,
      isRoundTrip: options?.idaVuelta || false,
      hasOversizeItem: options?.objetoFueraCaja || false,
      hasProductPayment: options?.productos || false,
      productAmount: this.costoProductosControl.value || 0,
      isRaining: options?.lluvia || false,
      
      isCommonHoliday: options?.feriadoComun || false,
      isSpecialHoliday: options?.feriadoEspecial || false,
      quantityWait10Minutes: (this.isEditMode && options?.tiempoEspera) ? (this.tiempoEsperaMinutosControl.value || 0) : 0,
      isOutOfCity: options?.recargoFueraDeCiudad || false,
      applyIVA: options?.iva || false
    };
    
    // Usar el nuevo método calculatePrice en lugar de calculateCost
    this.particularOrderService.calculatePrice(request).subscribe({
      next: (response) => {
        // Calculate return trip cost for display
        // Return trip cost = base cost + extra km cost (the cost that gets duplicated for round trip)
        const returnTripCost = response.baseCost + response.extraKmsCost;
        const subtotalCost = this.calculateBreakdownSubtotal(
          response.baseCost,
          response.extraKmsCost,
          returnTripCost,
          response.roundTripDiscount || 0,
          response.productFee || 0,
          response.oversizeFee || 0,
          response.rainSurcharge || 0,
          response.commonHolidayFee || 0,
          response.specialHolidayFee || 0,
          response.waitFee || 0,
          response.outOfCityFee || 0
        );
        const ivaPercentage = this.getIvaPercentage();
        const ivaAmount = options?.iva && ivaPercentage > 0 ? (subtotalCost * ivaPercentage) / 100 : 0;
        const finalTotalCost = subtotalCost + ivaAmount;
        
        // Guardar el desglose de costos completo usando los valores del backend
        this.costBreakdown = {
          baseCost: response.baseCost,
          extraKmsCost: response.extraKmsCost,
          returnTripCost: returnTripCost,
          roundTripDiscount: response.roundTripDiscount || 0,
          productFee: response.productFee || 0,
          oversizeFee: response.oversizeFee || 0,
          rainSurcharge: response.rainSurcharge || 0,
          feriadoComunSurcharge: response.commonHolidayFee || 0,
          feriadoEspecialSurcharge: response.specialHolidayFee || 0,
          waitFee: response.waitFee || 0,
          recargoFueraDeCiudadSurcharge: response.outOfCityFee || 0,
          ivaAmount,
          totalCost: subtotalCost,
          finalTotalCost
        };
        
        this.addressForm.patchValue({
          costo: subtotalCost.toFixed(2),
          finalCost: subtotalCost.toFixed(2),
        });

        if (options?.iva) {
          this.finalCostWithIVAControl.setValue(finalTotalCost.toFixed(2), { emitEvent: false });
        } else {
          this.finalCostWithIVAControl.setValue(null, { emitEvent: false });
        }

        this.loadingCost = false;
      },
      error: (error) => {
        this.loadingCost = false;
        this.showErrorDialog(
          'Error',
          'No se pudo calcular el costo. Por favor, intente nuevamente.'
        );
      },
    });
  }

  getWaypointFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  addWaypoint(): void {
    const waypointGroup = this.fb.group({
      address: ['', Validators.required],
      addressDetail: [''],
      latitude: [null],
      longitude: [null],
    });

    // Subscribe to address changes to update lat/lng
    const addressControl = waypointGroup.get('address');
    if (addressControl) {
      addressControl.valueChanges
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((address: string) => {
          if (!address || address.length < 4) return;

          this.googleMapsService
            .geocodeAddress(address)
            .then((results: any[]) => {
              if (
                results &&
                results.length > 0 &&
                results[0].geometry?.location
              ) {
                const location = results[0].geometry.location;
                waypointGroup.patchValue(
                  {
                    latitude: location.lat(),
                    longitude: location.lng(),
                  },
                  { emitEvent: false }
                );
              }
            })
            .catch((error: any) => {
              console.error('Error geocoding waypoint address:', error);
            });
        });
    }

    this.waypointsArray.push(waypointGroup);
  }

  removeWaypoint(index: number): void {
    this.waypointsArray.removeAt(index);
  }

  moveAddress(type: 'pickup' | 'delivery', direction: 'up' | 'down'): void {
    if (type === 'pickup' && direction === 'up') return;
    if (type === 'delivery' && direction === 'down') return;

    // If no intermediate waypoints, swap pickup and delivery data directly.
    if (this.waypointsArray.length === 0) {
      if (type === 'pickup' || type === 'delivery') {
        const pickupAddress = this.addressForm.get('pickupAddress')?.value;
        const pickupAddressDetail = this.addressForm.get('pickupAddressDetail')?.value;
        const deliveryAddress = this.addressForm.get('deliveryAddress')?.value;
        const deliveryAddressDetail = this.addressForm.get('deliveryAddressDetail')?.value;

        // Swap address + detail as a single unit.
        this.addressForm.get('pickupAddress')?.setValue(deliveryAddress);
        this.addressForm.get('pickupAddressDetail')?.setValue(deliveryAddressDetail);
        this.addressForm.get('deliveryAddress')?.setValue(pickupAddress);
        this.addressForm.get('deliveryAddressDetail')?.setValue(pickupAddressDetail);

        const tempLat = this.pickupLat;
        const tempLng = this.pickupLng;
        this.pickupLat = this.deliveryLat;
        this.pickupLng = this.deliveryLng;
        this.deliveryLat = tempLat;
        this.deliveryLng = tempLng;

        this.addressForm.markAsDirty();
      }
      return;
    }

    if (type === 'pickup' && direction === 'down') {
      const firstWaypointControl = this.waypointsArray.at(0);
      const pickupAddress = this.addressForm.get('pickupAddress')?.value;
      const pickupAddressDetail = this.addressForm.get('pickupAddressDetail')?.value;
      const pickupLatitude = this.pickupLat;
      const pickupLongitude = this.pickupLng;
      const firstWaypointAddress = firstWaypointControl.get('address')?.value;
      const firstWaypointAddressDetail = firstWaypointControl.get('addressDetail')?.value;
      const firstWaypointLatitude = firstWaypointControl.get('latitude')?.value;
      const firstWaypointLongitude = firstWaypointControl.get('longitude')?.value;

      this.addressForm.get('pickupAddress')?.setValue(firstWaypointAddress || '');
      this.addressForm.get('pickupAddressDetail')?.setValue(firstWaypointAddressDetail || '');
      this.pickupLat = firstWaypointLatitude ?? null;
      this.pickupLng = firstWaypointLongitude ?? null;

      firstWaypointControl.get('address')?.setValue(pickupAddress || '');
      firstWaypointControl.get('addressDetail')?.setValue(pickupAddressDetail || '');
      firstWaypointControl.get('latitude')?.setValue(pickupLatitude ?? null);
      firstWaypointControl.get('longitude')?.setValue(pickupLongitude ?? null);
    } else if (type === 'delivery' && direction === 'up') {
      const lastIndex = this.waypointsArray.length - 1;
      const lastWaypointControl = this.waypointsArray.at(lastIndex);
      const deliveryAddress = this.addressForm.get('deliveryAddress')?.value;
      const deliveryAddressDetail = this.addressForm.get('deliveryAddressDetail')?.value;
      const deliveryLatitude = this.deliveryLat;
      const deliveryLongitude = this.deliveryLng;
      const lastWaypointAddress = lastWaypointControl.get('address')?.value;
      const lastWaypointAddressDetail = lastWaypointControl.get('addressDetail')?.value;
      const lastWaypointLatitude = lastWaypointControl.get('latitude')?.value;
      const lastWaypointLongitude = lastWaypointControl.get('longitude')?.value;

      this.addressForm.get('deliveryAddress')?.setValue(lastWaypointAddress || '');
      this.addressForm.get('deliveryAddressDetail')?.setValue(lastWaypointAddressDetail || '');
      this.deliveryLat = lastWaypointLatitude ?? null;
      this.deliveryLng = lastWaypointLongitude ?? null;

      lastWaypointControl.get('address')?.setValue(deliveryAddress || '');
      lastWaypointControl.get('addressDetail')?.setValue(deliveryAddressDetail || '');
      lastWaypointControl.get('latitude')?.setValue(deliveryLatitude ?? null);
      lastWaypointControl.get('longitude')?.setValue(deliveryLongitude ?? null);
    }

    this.waypoints = [...this.waypointsArray.value];
    this.waypointsChange.emit([...this.waypoints]);
    this.addressForm.markAsDirty();
  }

  moveWaypoint(index: number, direction: 'up' | 'down'): void {
    if (direction === 'up') {
      if (index === 0) return;
      this.swapWithAdjacent(index, index - 1);
    } else {
      if (index === this.waypointsArray.length - 1) return;
      this.swapWithAdjacent(index, index + 1);
    }
  }

  private swapWithAdjacent(index1: number, index2: number): void {
    const formArray = this.waypointsArray;

    const temp = formArray.at(index1);
    formArray.setControl(index1, formArray.at(index2));
    formArray.setControl(index2, temp);

    this.waypoints = [...formArray.value];
    this.waypointsChange.emit([...this.waypoints]);
    this.addressForm.markAsDirty();
  }

  // Load pricing rules from backend
  loadPricingRules(): void {
    this.deliveryPricingRulesService.getAllDeliveryPricingRules().subscribe({
      next: (rules) => {
        // Filter active rules
        this.pricingRules = rules.filter(rule => rule.isActive);
        this.ivaPercentage = this.pricingRules.find(rule => rule.category === Category.IVA)?.value ?? null;

        // Reprocesar datos de edición cuando las reglas ya están disponibles.
        if (this.isEditMode && this.particularOrderPricingDetail?.length > 0) {
          this.updateCheckboxesFromPricingDetails(this.particularOrderPricingDetail);
          this.generateCostBreakdownFromPricingDetails(this.particularOrderPricingDetail);
        }
      },
      error: (error) => {
        console.error('Error loading pricing rules:', error);
      }
    });
  }

  // Update selectedPricingRuleIds when checkboxes change
  setupCheckboxListeners(): void {
    // Listen for ida y vuelta checkbox changes
    this.optionsFormGroup.get('idaVuelta')?.valueChanges.subscribe(checked => {
      this.updatePricingRuleSelection(Category.DescuentoIdaVuelta, checked);
    });

    // Listen for objeto fuera de caja checkbox changes
    this.optionsFormGroup.get('objetoFueraCaja')?.valueChanges.subscribe(checked => {
      this.updatePricingRuleSelection(Category.CargoSobredimension, checked);
    });

    // Listen for lluvia checkbox changes
    this.optionsFormGroup.get('lluvia')?.valueChanges.subscribe(checked => {
      this.updatePricingRuleSelection(Category.RecargoLluviaBase, checked);
    });

    // Listen for feriado común checkbox changes
    this.optionsFormGroup.get('feriadoComun')?.valueChanges.subscribe(checked => {
      this.updatePricingRuleSelection(Category.FeriadoComun, checked);
    });

    // Listen for feriado especial checkbox changes
    this.optionsFormGroup.get('feriadoEspecial')?.valueChanges.subscribe(checked => {
      this.updatePricingRuleSelection(Category.FeriadoEspecial, checked);
    });

    // Listen for tiempoEspera checkbox changes
    this.optionsFormGroup.get('tiempoEspera')?.valueChanges.subscribe(checked => {
      this.updatePricingRuleSelection(Category.TarifaEsperaCada10Min, checked);
    });

    // Listen for recargoFueraDeCiudad checkbox changes
    this.optionsFormGroup.get('recargoFueraDeCiudad')?.valueChanges.subscribe(checked => {
      this.updatePricingRuleSelection(Category.RecargoFueraDeCiudad, checked);
    });

    // Listen for iva checkbox changes
    this.optionsFormGroup.get('iva')?.valueChanges.subscribe(checked => {
      this.updatePricingRuleSelection(Category.IVA, checked);
    });
  }

  // Helper method to update selectedPricingRuleIds based on checkbox changes
  updatePricingRuleSelection(category: Category, isSelected: boolean): void {
    // Find pricing rules with the matching category
    const matchingRules = this.pricingRules.filter(rule => rule.category === category);
    
    if (isSelected) {
      // Add rule IDs to selectedPricingRuleIds if not already present
      matchingRules.forEach(rule => {
        if (!this.selectedPricingRuleIds.includes(rule.id)) {
          this.selectedPricingRuleIds.push(rule.id);
        }
      });
    } else {
      // Remove rule IDs from selectedPricingRuleIds
      this.selectedPricingRuleIds = this.selectedPricingRuleIds.filter(
        id => !matchingRules.some(rule => rule.id === id)
      );
    }
  }
  
  // Set checkbox states based on selected pricing rules
  updateCheckboxesFromPricingRules(): void {
    // Temporarily remove listeners to avoid circular updates
    const idaVueltaControl = this.optionsFormGroup.get('idaVuelta');
    const objetoFueraCajaControl = this.optionsFormGroup.get('objetoFueraCaja');
    const lluviaControl = this.optionsFormGroup.get('lluvia');
    const feriadoComunControl = this.optionsFormGroup.get('feriadoComun');
    const feriadoEspecialControl = this.optionsFormGroup.get('feriadoEspecial');
    
    // Check if we have any pricing rules for each category
    const hasIdaVueltaRule = this.selectedPricingRuleIds.some(id => 
      this.pricingRules.some(rule => rule.id === id && rule.category === Category.DescuentoIdaVuelta)
    );
    
    const hasObjetoFueraCajaRule = this.selectedPricingRuleIds.some(id => 
      this.pricingRules.some(rule => rule.id === id && rule.category === Category.CargoSobredimension)
    );
    
    const hasLluviaRule = this.selectedPricingRuleIds.some(id => 
      this.pricingRules.some(rule => rule.id === id && rule.category === Category.RecargoLluviaBase)
    );
    
    const hasFeriadoComunRule = this.selectedPricingRuleIds.some(id => 
      this.pricingRules.some(rule => rule.id === id && rule.category === Category.FeriadoComun)
    );
    
    const hasFeriadoEspecialRule = this.selectedPricingRuleIds.some(id => 
      this.pricingRules.some(rule => rule.id === id && rule.category === Category.FeriadoEspecial)
    );
    

    const hasTiempoEsperaRule = this.selectedPricingRuleIds.some(id => 
      this.pricingRules.some(rule => rule.id === id && rule.category === Category.TarifaEsperaCada10Min)
    );
    
    const hasRecargoFueraDeCiudadRule = this.selectedPricingRuleIds.some(id => 
      this.pricingRules.some(rule => rule.id === id && rule.category === Category.RecargoFueraDeCiudad)
    );
    
    const hasIvaRule = this.selectedPricingRuleIds.some(id => 
      this.pricingRules.some(rule => rule.id === id && rule.category === Category.IVA)
    );
    
    // Set checkbox values
    if (idaVueltaControl) idaVueltaControl.setValue(hasIdaVueltaRule, { emitEvent: false });
    if (objetoFueraCajaControl) objetoFueraCajaControl.setValue(hasObjetoFueraCajaRule, { emitEvent: false });
    if (lluviaControl) lluviaControl.setValue(hasLluviaRule, { emitEvent: false });
    if (feriadoComunControl) feriadoComunControl.setValue(hasFeriadoComunRule, { emitEvent: false });
    if (feriadoEspecialControl) feriadoEspecialControl.setValue(hasFeriadoEspecialRule, { emitEvent: false });
    
    const tiempoEsperaControl = this.optionsFormGroup.get('tiempoEspera');
    if (tiempoEsperaControl) tiempoEsperaControl.setValue(hasTiempoEsperaRule, { emitEvent: false });
    
    const recargoFueraDeCiudadControl = this.optionsFormGroup.get('recargoFueraDeCiudad');
    if (recargoFueraDeCiudadControl) recargoFueraDeCiudadControl.setValue(hasRecargoFueraDeCiudadRule, { emitEvent: false });
    
    const ivaControl = this.optionsFormGroup.get('iva');
    if (ivaControl) ivaControl.setValue(hasIvaRule, { emitEvent: false });
  }

  // Set checkbox states based on pricing details from backend
  updateCheckboxesFromPricingDetails(pricingDetails: { deliveryPricingRuleId: number; value: number }[]): void {
    const idaVueltaControl = this.optionsFormGroup.get('idaVuelta');
    const objetoFueraCajaControl = this.optionsFormGroup.get('objetoFueraCaja');
    const lluviaControl = this.optionsFormGroup.get('lluvia');
    const productosControl = this.optionsFormGroup.get('productos');
    const feriadoComunControl = this.optionsFormGroup.get('feriadoComun');
    const feriadoEspecialControl = this.optionsFormGroup.get('feriadoEspecial');
    const tiempoEsperaControl = this.optionsFormGroup.get('tiempoEspera');
    const recargoFueraDeCiudadControl = this.optionsFormGroup.get('recargoFueraDeCiudad');
    const ivaControl = this.optionsFormGroup.get('iva');
    
    // Reset all checkboxes first
    if (idaVueltaControl) idaVueltaControl.setValue(false, { emitEvent: false });
    if (objetoFueraCajaControl) objetoFueraCajaControl.setValue(false, { emitEvent: false });
    if (lluviaControl) lluviaControl.setValue(false, { emitEvent: false });
    if (productosControl) productosControl.setValue(false, { emitEvent: false });
    if (feriadoComunControl) feriadoComunControl.setValue(false, { emitEvent: false });
    if (feriadoEspecialControl) feriadoEspecialControl.setValue(false, { emitEvent: false });
    if (tiempoEsperaControl) tiempoEsperaControl.setValue(false, { emitEvent: false });
    if (recargoFueraDeCiudadControl) recargoFueraDeCiudadControl.setValue(false, { emitEvent: false });
    if (ivaControl) ivaControl.setValue(false, { emitEvent: false });
    
    this.ivaPercentage = null;

    // Process each pricing detail
    pricingDetails.forEach(detail => {
      // Find the corresponding pricing rule
      const matchingRule = this.pricingRules.find(rule => rule.id === detail.deliveryPricingRuleId);
      
      if (matchingRule) {
        // Add the rule ID to selectedPricingRuleIds if not already present
        if (!this.selectedPricingRuleIds.includes(matchingRule.id)) {
          this.selectedPricingRuleIds.push(matchingRule.id);
        }
        
        // Mark specific options based on category
        switch (matchingRule.category) {
          case Category.DescuentoIdaVuelta:
            if (idaVueltaControl) idaVueltaControl.setValue(true, { emitEvent: false });
            break;
          case Category.CargoSobredimension:
            if (objetoFueraCajaControl) objetoFueraCajaControl.setValue(true, { emitEvent: false });
            break;
          case Category.RecargoLluviaBase:
          case Category.RecargoLluviaPorKm:
            if (lluviaControl) lluviaControl.setValue(true, { emitEvent: false });
            break;
          case Category.ComisionPorPago:
            if (productosControl && detail.value > 0) {
              productosControl.setValue(true, { emitEvent: false });
              
              // Obtener la regla de ComisionPorPago desde la API para obtener el porcentaje correcto
              this.deliveryPricingRulesService.getDeliveryPricingRuleById(detail.deliveryPricingRuleId).subscribe({
                next: (comisionRule) => {
                  if (comisionRule && comisionRule.value > 0) {
                    // Calcular el costo original del producto: commission_amount * 100 / percentage
                    // Ejemplo: si commission_amount = 600 y percentage = 12, entonces original = (600 * 100) / 12 = 5000
                    const originalProductCost = (detail.value * 100) / comisionRule.value;
                    this.addressForm.get('costoProductos')?.setValue(originalProductCost, { emitEvent: false });
                  } else {
                    // Si la regla no tiene un valor válido, usar el valor guardado como fallback
                    this.addressForm.get('costoProductos')?.setValue(detail.value, { emitEvent: false });
                  }
                },
                error: (error) => {
                  console.error('Error fetching pricing rule for reverse calculation:', error);
                  // En caso de error, usar el valor guardado como fallback
                  this.addressForm.get('costoProductos')?.setValue(detail.value, { emitEvent: false });
                }
              });
            }
            break;
          case Category.TarifaEsperaCada10Min:
            if (tiempoEsperaControl && detail.value > 0) {
              tiempoEsperaControl.setValue(true, { emitEvent: false });
              
              // Obtener la regla de TarifaEsperaCada10Min para calcular la cantidad de demoras originales
              this.deliveryPricingRulesService.getDeliveryPricingRuleById(detail.deliveryPricingRuleId).subscribe({
                next: (esperaRule) => {
                  if (esperaRule && esperaRule.value > 0) {
                    // Calcular la cantidad de demoras originales: total_cost / cost_per_10_minutes
                    // Ejemplo: si total_cost = 150 y cost_per_10_minutes = 50, entonces demoras = 150 / 50 = 3
                    const originalDemoras = Math.round(detail.value / esperaRule.value);
                    this.addressForm.get('tiempoEsperaMinutos')?.setValue(originalDemoras, { emitEvent: false });
                  } else {
                    // Si la regla no tiene un valor válido, usar un valor por defecto
                    this.addressForm.get('tiempoEsperaMinutos')?.setValue(1, { emitEvent: false });
                  }
                },
                error: (error) => {
                  console.error('Error fetching pricing rule for reverse calculation:', error);
                  // En caso de error, usar un valor por defecto
                  this.addressForm.get('tiempoEsperaMinutos')?.setValue(1, { emitEvent: false });
                }
              });
            }
            break;
          case Category.FeriadoComun:
            if (feriadoComunControl) feriadoComunControl.setValue(true, { emitEvent: false });
            break;
          case Category.FeriadoEspecial:
            if (feriadoEspecialControl) feriadoEspecialControl.setValue(true, { emitEvent: false });
            break;
          case Category.RecargoFueraDeCiudad:
            if (recargoFueraDeCiudadControl) recargoFueraDeCiudadControl.setValue(true, { emitEvent: false });
            break;
          case Category.IVA:
            if (ivaControl) ivaControl.setValue(true, { emitEvent: false });
            this.deliveryPricingRulesService.getDeliveryPricingRuleById(detail.deliveryPricingRuleId).subscribe({
              next: (ivaRule) => {
                if (ivaRule && ivaRule.value > 0) {
                  this.ivaPercentage = ivaRule.value;
                  const finalCostValue = this.addressForm.get('finalCost')?.value;
                  this.calculateFinalCostWithIVA(finalCostValue);
                }
              },
              error: (error) => {
                console.error('Error fetching IVA pricing rule:', error);
              }
            });
            break;
        }
      }
    });
    
    // Generate cost breakdown for edit mode after processing pricing details
    this.generateCostBreakdownFromPricingDetails(pricingDetails);

    if (this.ivaPercentage === null) {
      this.ivaPercentage = this.pricingRules.find(rule => rule.category === Category.IVA)?.value ?? null;
    }
  }

  // Generate cost breakdown from existing pricing details (for edit mode)
  generateCostBreakdownFromPricingDetails(pricingDetails: { deliveryPricingRuleId: number; value: number }[]): void {
    const formCalculatedCost = parseFloat(this.addressForm.get('costo')?.value || '0');
    
    // Initialize breakdown with default values
    let baseCost = 0;
    let extraKmsCost = 0;
    let roundTripDiscount = 0;
    let productFee = 0;
    let oversizeFee = 0;
    let rainSurcharge = 0;
    let feriadoComunSurcharge = 0;
    let feriadoEspecialSurcharge = 0;
    let waitFee = 0;
    let recargoFueraDeCiudadSurcharge = 0;
    let ivaAmount = 0;
    let hasIvaDetail = false;
    
    // Process each pricing detail to extract values
    pricingDetails.forEach(detail => {
      const matchingRule = this.pricingRules.find(rule => rule.id === detail.deliveryPricingRuleId);
      
      if (matchingRule) {
        switch (matchingRule.category) {
          case Category.DescuentoIdaVuelta:
            roundTripDiscount = detail.value;
            break;
          case Category.CargoSobredimension:
            oversizeFee = detail.value;
            break;
          case Category.RecargoLluviaBase:
            rainSurcharge += detail.value;
            break;
          case Category.RecargoLluviaPorKm:
            rainSurcharge += detail.value;
            break;
          case Category.ComisionPorPago:
            productFee = detail.value;
            break;
          case Category.FeriadoComun:
            feriadoComunSurcharge = detail.value;
            break;
          case Category.FeriadoEspecial:
            feriadoEspecialSurcharge = detail.value;
            break;
          case Category.TarifaPorKmExtra:
            extraKmsCost = detail.value;
            break;
          case Category.TarifaEsperaCada10Min:
            waitFee = detail.value;
            break;
          case Category.RecargoFueraDeCiudad:
            recargoFueraDeCiudadSurcharge = detail.value;
            break;
          case Category.IVA:
            hasIvaDetail = true;
            break;
        }
      }
    });
    
    // Store the extraKmsCost from pricing details before any recalculation
    const extraKmsCostFromPricingDetails = extraKmsCost;
    
    // In edit mode, calculate base cost from pricing rules, not from total cost
    // Get the base tariff from pricing rules
    const baseDistanceRule = this.pricingRules.find(r => r.category === Category.TarifaBase);
    if (baseDistanceRule) {
      baseCost = baseDistanceRule.value; // Use the base tariff directly
    } else {
      // Fallback: derive base cost from total if no base rule found
      baseCost = formCalculatedCost
        - extraKmsCost
        - productFee
        - oversizeFee
        - rainSurcharge
        - feriadoComunSurcharge
        - feriadoEspecialSurcharge
        - waitFee
        - recargoFueraDeCiudadSurcharge
        + roundTripDiscount;
    }
    
    // Only recalculate extra km cost if we don't have it from pricing details
    // This happens when creating a new order or when pricing details don't include these values
    if (extraKmsCostFromPricingDetails === 0 && this.totalDistanceKm > 0) {
      const extraKmRule = this.pricingRules.find(r => r.category === Category.TarifaPorKmExtra);
      const baseDistanceRule2 = this.pricingRules.find(r => r.category === Category.DistanciaBase);
      
      if (baseDistanceRule && extraKmRule && baseDistanceRule2) {
        const baseDistance = baseDistanceRule2.value; // Base distance in km
        const extraKmTariff = extraKmRule.value; // Extra km tariff
        
        if (this.totalDistanceKm > baseDistance) {
          extraKmsCost = (this.totalDistanceKm - baseDistance) * extraKmTariff;
        }
      }
    }
    
    // Calculate return trip cost for display
    // Return trip cost = base cost + extra km cost (the cost that gets duplicated)
    const returnTripCost = baseCost + extraKmsCost;
    const subtotalCost = this.calculateBreakdownSubtotal(
      baseCost,
      extraKmsCost,
      returnTripCost,
      roundTripDiscount,
      productFee,
      oversizeFee,
      rainSurcharge,
      feriadoComunSurcharge,
      feriadoEspecialSurcharge,
      waitFee,
      recargoFueraDeCiudadSurcharge
    );

    if (hasIvaDetail) {
      const ivaPercentage = this.getIvaPercentage();
      ivaAmount = ivaPercentage > 0 ? (subtotalCost * ivaPercentage) / 100 : 0;
    }

    const finalTotalCost = subtotalCost + ivaAmount;
    
    // Set the cost breakdown
    this.costBreakdown = {
      baseCost,
      extraKmsCost,
      returnTripCost,
      roundTripDiscount,
      productFee,
      oversizeFee,
      rainSurcharge,
      feriadoComunSurcharge,
      feriadoEspecialSurcharge,
      waitFee,
      recargoFueraDeCiudadSurcharge,
      ivaAmount,
      totalCost: subtotalCost > 0 ? subtotalCost : formCalculatedCost,
      finalTotalCost: finalTotalCost > 0 ? finalTotalCost : formCalculatedCost
    };

    const ivaChecked = this.optionsFormGroup.get('iva')?.value;
    if (ivaChecked) {
      this.finalCostWithIVAControl.setValue(this.costBreakdown.finalTotalCost.toFixed(2), { emitEvent: false });
    } else {
      this.finalCostWithIVAControl.setValue(null, { emitEvent: false });
    }
  }

  ngOnInit(): void {
    // Fetch pricing rules
    this.loadPricingRules();
    
    // Load clients for autocomplete
    this.loadClients();
    
    // Setup checkbox listeners
    this.setupCheckboxListeners();
    
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.isEditMode = true;
        this.orderId = Number(id);
        this.particularOrderService
          .getParticularOrderById(this.orderId)
          .subscribe((order) => {
            // Store the order status for address editing validation
            this.orderStatus = order.status ?? null;
            
            // First, patch the basic form values
            this.addressForm.patchValue({
              pickupAddress: order.originAddress,
              pickupAddressDetail: '',
              deliveryAddress: order.destinationAddress,
              deliveryAddressDetail: '',
              personName: order.recipientName,
              pickupTimeRange: order.pickupTimeRange,
              deliveryTimeRange: order.deliveryTimeRange,
              pickupPhone: order.pickupPhone,
              deliveryPhone: order.deliveryPhone,
              observations: order.observations,
              paymentLocation: this.getPaymentLocationString(order.whereToPay),
              costo: order.calculatedCost ?? null,
              options: {
                ...this.addressForm.get('options')?.value,
                isScheduled: !!order.scheduledAt,
                scheduledAt: order.scheduledAt ? new Date(order.scheduledAt) : null,
              },     
              finalCost: order.finalCost ?? null,
            });

            // Set selected client if exists
            if (order.cdClient) {
              this.selectedClientId = order.cdClient;
            }
            this.originalPersonName = (order.recipientName || '').trim();
            
            // Set calculated cost separately for disabled control
            if (order.calculatedCost !== null && order.calculatedCost !== undefined) {
              this.addressForm.get('costo')?.setValue(order.calculatedCost);
            }
            
            // Generate cost breakdown from pricing details if they exist
            if (order.particularOrderPricingDetail && order.particularOrderPricingDetail.length > 0) {
              this.particularOrderPricingDetail = [...order.particularOrderPricingDetail];
              this.generateCostBreakdownFromPricingDetails(order.particularOrderPricingDetail);
              
              // Marcar las opciones basadas en los detalles de pricing
              this.updateCheckboxesFromPricingDetails(order.particularOrderPricingDetail);
            } else {
              // Si no hay detalles, usar los pricing rule IDs para marcar checkboxes
              this.updateCheckboxesFromPricingRules();
            }

            // Set the coordinates
            this.pickupLat = order.originLatitude ?? null;
            this.pickupLng = order.originLongitude ?? null;
            this.deliveryLat = order.destinationLatitude ?? null;
            this.deliveryLng = order.destinationLongitude ?? null;
            this.distanceKm = order.distanceKm ?? null;

            // Set the distance display
            if (this.distanceKm !== null) {
              this.distance = this.distanceKm.toFixed(2);
              this.totalDistanceKm = this.distanceKm;
            }
            
            // Set pricing rule IDs if they exist
            if (order.pricingRuleIds && order.pricingRuleIds.length > 0) {
              this.selectedPricingRuleIds = [...order.pricingRuleIds];
            }
            
            // Set pricing details if they exist
            if (order.particularOrderPricingDetail && order.particularOrderPricingDetail.length > 0) {
              this.particularOrderPricingDetail = [...order.particularOrderPricingDetail];
              
              // Marcar las opciones basadas en los detalles de pricing
              this.updateCheckboxesFromPricingDetails(order.particularOrderPricingDetail);
            } else {
              // Si no hay detalles, usar los pricing rule IDs para marcar checkboxes
              this.updateCheckboxesFromPricingRules();
            }

            // Load waypoints if they exist
            if (order.waypoints && order.waypoints.length > 0) {
              // Store original waypoints with IDs for comparison
              this.originalWaypoints = [...order.waypoints];
              
              // Clear any existing waypoints
              while (this.waypointsArray.length) {
                this.waypointsArray.removeAt(0);
              }

              // Sort waypoints by order index
              const sortedWaypoints = [...order.waypoints].sort(
                (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
              );

              // Since pickup and delivery are now stored as waypoints for traceability,
              // we need to extract them and only load intermediate waypoints
              if (sortedWaypoints.length >= 2) {
                // First waypoint is pickup address
                const pickupWaypoint = sortedWaypoints[0];
                this.addressForm.patchValue({
                  pickupAddress: pickupWaypoint.address,
                  pickupAddressDetail: pickupWaypoint.addressDetail || '',
                });
                this.pickupLat = pickupWaypoint.latitude ?? null;
                this.pickupLng = pickupWaypoint.longitude ?? null;

                // Last waypoint is delivery address  
                const deliveryWaypoint = sortedWaypoints[sortedWaypoints.length - 1];
                this.addressForm.patchValue({
                  deliveryAddress: deliveryWaypoint.address,
                  deliveryAddressDetail: deliveryWaypoint.addressDetail || '',
                });
                this.deliveryLat = deliveryWaypoint.latitude ?? null;
                this.deliveryLng = deliveryWaypoint.longitude ?? null;

                // Only add intermediate waypoints (excluding first and last)
                const intermediateWaypoints = sortedWaypoints.slice(1, -1);
                intermediateWaypoints.forEach((waypoint) => {
                  const waypointGroup = this.fb.group({
                    address: [waypoint.address, Validators.required],
                    addressDetail: [waypoint.addressDetail || ''],
                    latitude: [waypoint.latitude],
                    longitude: [waypoint.longitude],
                  });
                  this.waypointsArray.push(waypointGroup);
                });
              } else {
                // Fallback: if less than 2 waypoints, load them all as intermediate waypoints
                sortedWaypoints.forEach((waypoint) => {
                  const waypointGroup = this.fb.group({
                    address: [waypoint.address, Validators.required],
                    addressDetail: [waypoint.addressDetail || ''],
                    latitude: [waypoint.latitude],
                    longitude: [waypoint.longitude],
                  });
                  this.waypointsArray.push(waypointGroup);
                });
              }
            }
            
            // Set finalCostWithIVA AFTER checkboxes have been updated
            // Use setTimeout to ensure checkboxes are processed first
            setTimeout(() => {
              if (order.finalCostWithIVA !== null && order.finalCostWithIVA !== undefined) {
                this.finalCostWithIVAControl.setValue(order.finalCostWithIVA.toFixed(2), { emitEvent: false });
              }
            }, 0);
          });
      }
    });
  }

  getPaymentLocationString(location: PaymentLocation): string {
    switch (location) {
      case PaymentLocation.Destino:
        return 'Destino';
      case PaymentLocation.Origen:
        return 'Entrega';
      case PaymentLocation.Cuenta:
        return 'Cuenta';
      default:
        return '';
    }
  }

  getPaymentLocationValue(locationString: string): PaymentLocation {
    switch (locationString) {
      case 'Destino':
        return PaymentLocation.Destino;
      case 'Entrega':
        return PaymentLocation.Origen;
      case 'Cuenta':
        return PaymentLocation.Cuenta;
      default:
        return PaymentLocation.Destino; // Valor por defecto
    }
  }

  async calculateDistance() {
    const formValue = this.addressForm.value;
    
    // Verificar que las direcciones obligatorias estén ingresadas
    if (!formValue.pickupAddress || !formValue.deliveryAddress) {
      this.showErrorDialog(
        'Error',
        'No se pudo calcular la distancia. Verificá las direcciones ingresadas.'
      );
      return;
    }

    // Verificar que todas las paradas intermedias tengan dirección
    if (formValue.waypoints && formValue.waypoints.some((w: any) => !w.address)) {
      this.showErrorDialog(
        'Error',
        'No se pudo calcular la distancia. Verificá las direcciones ingresadas.'
      );
      return;
    }

    this.loadingDistance = true;
    this.distance = null;
    this.totalDistanceKm = 0;
    this.waypoints = [];

    try {
      const addresses = [
        { type: 'pickup', address: formValue.pickupAddress },
        ...formValue.waypoints.map((w: any) => ({
          type: 'waypoint',
          address: w.address,
        })),
        { type: 'delivery', address: formValue.deliveryAddress },
      ];

      let geocoded;
      try {
        geocoded = await Promise.all(
          addresses.map(async (addr) => {
            try {
              const geo = await this.googleMapsService.geocodeAddress(
                addr.address
              );
              return {
                type: addr.type,
                location: geo.results[0]?.geometry?.location,
                address: addr.address,
              };
            } catch (error) {
              // No mostrar error individual, será manejado más abajo
              return { type: addr.type, location: null, address: addr.address };
            }
          })
        );

        const invalidAddress = geocoded.find((g) => !g.location);
        if (invalidAddress) {
          this.showErrorDialog(
            'Error',
            'No se pudo calcular la distancia. Verificá las direcciones ingresadas.'
          );
          this.loadingDistance = false;
          return;
        }
      } catch (error) {
        this.showErrorDialog(
          'Error',
          'No se pudo calcular la distancia. Verificá las direcciones ingresadas.'
        );
        this.loadingDistance = false;
        return;
      }

      this.waypoints = geocoded.filter((g) => g.type === 'waypoint');

      let totalDistanceMeters = 0;

      for (let i = 0; i < geocoded.length - 1; i++) {
        const origin = geocoded[i];
        const destination = geocoded[i + 1];

        try {
          const distanceResponse = await this.googleMapsService.getDistance(
            `${origin.location.lat},${origin.location.lng}`,
            `${destination.location.lat},${destination.location.lng}`
          );

          const distanceInMeters =
            distanceResponse.rows[0]?.elements[0]?.distance?.value;
          if (distanceInMeters === undefined) {
            this.showErrorDialog(
              'Error',
              'No se pudo calcular la distancia. Verificá las direcciones ingresadas.'
            );
            this.loadingDistance = false;
            return;
          }

          totalDistanceMeters += distanceInMeters;
        } catch (error: unknown) {
          this.showErrorDialog(
            'Error',
            'No se pudo calcular la distancia. Verificá las direcciones ingresadas.'
          );
          this.loadingDistance = false;
          return;
        }
      }

      this.totalDistanceKm = totalDistanceMeters / 1000;
      this.distance = this.totalDistanceKm.toFixed(2);

      const pickup = geocoded[0];
      const delivery = geocoded[geocoded.length - 1];
      this.pickupLat = pickup.location.lat;
      this.pickupLng = pickup.location.lng;
      this.deliveryLat = delivery.location.lat;
      this.deliveryLng = delivery.location.lng;
      this.distanceKm = this.totalDistanceKm;
    } catch (error: unknown) {
      this.showErrorDialog(
        'Error',
        'No se pudo calcular la distancia. Verificá las direcciones ingresadas.'
      );
    } finally {
      this.loadingDistance = false;
    }
  }

  // Genera los detalles de pricing basados en las opciones seleccionadas
  // Envía las categorías opcionales y TarifaPorKmExtra para que aparezca en el desglose en modo edición
  generatePricingDetails(): { deliveryPricingRuleId: number; value: number }[] {
    const formData = this.addressForm.value;
    const pricingDetails: { deliveryPricingRuleId: number; value: number }[] = [];
    
    // DescuentoIdaVuelta - si está seleccionada la opción ida y vuelta
    if (formData.options.idaVuelta) {
      const idaVueltaRule = this.pricingRules.find(r => r.category === Category.DescuentoIdaVuelta);
      if (idaVueltaRule && this.costBreakdown) {
        // Enviar el monto adicional que se suma por ida y vuelta
        // Si el viaje vale 2000 y se le suma 1000, se envía 1000
        pricingDetails.push({
          deliveryPricingRuleId: idaVueltaRule.id,
          value: this.costBreakdown.roundTripDiscount // Monto adicional por ida y vuelta
        });
      }
    }
    
    // CargoSobredimension - si está seleccionada la opción objeto fuera de caja
    if (formData.options.objetoFueraCaja) {
      const sobredimensionRule = this.pricingRules.find(r => r.category === Category.CargoSobredimension);
      if (sobredimensionRule) {
        pricingDetails.push({
          deliveryPricingRuleId: sobredimensionRule.id,
          value: sobredimensionRule.value // Cargo fijo por sobredimensión
        });
      }
    }
    
    // RecargoLluviaBase y RecargoLluviaPorKm - si está seleccionada la opción lluvia
    if (formData.options.lluvia) {
      const lluviaBaseRule = this.pricingRules.find(r => r.category === Category.RecargoLluviaBase);
      if (lluviaBaseRule) {
        pricingDetails.push({
          deliveryPricingRuleId: lluviaBaseRule.id,
          value: lluviaBaseRule.value // Recargo base por lluvia
        });
      }
      
      const lluviaPorKmRule = this.pricingRules.find(r => r.category === Category.RecargoLluviaPorKm);
      if (lluviaPorKmRule) {
        pricingDetails.push({
          deliveryPricingRuleId: lluviaPorKmRule.id,
          value: lluviaPorKmRule.value // Recargo por km en lluvia
        });
      }
    }
    
    // FeriadoComun - si está seleccionada la opción feriado común
    if (formData.options.feriadoComun) {
      const feriadoComunRule = this.pricingRules.find(r => r.category === Category.FeriadoComun);
      if (feriadoComunRule && this.costBreakdown) {
        // Priorizar el valor real del desglose para mantener consistencia con el cálculo mostrado.
        const feriadoComunAmount = this.costBreakdown.feriadoComunSurcharge > 0
          ? this.costBreakdown.feriadoComunSurcharge
          : (this.costBreakdown.baseCost * feriadoComunRule.value) / 100;
        pricingDetails.push({
          deliveryPricingRuleId: feriadoComunRule.id,
          value: feriadoComunAmount
        });
      }
    }
    
    // FeriadoEspecial - si está seleccionada la opción feriado especial
    if (formData.options.feriadoEspecial) {
      const feriadoEspecialRule = this.pricingRules.find(r => r.category === Category.FeriadoEspecial);
      if (feriadoEspecialRule && this.costBreakdown) {
        // Priorizar el valor real del desglose para mantener consistencia con el cálculo mostrado.
        const feriadoEspecialAmount = this.costBreakdown.feriadoEspecialSurcharge > 0
          ? this.costBreakdown.feriadoEspecialSurcharge
          : (this.costBreakdown.baseCost * feriadoEspecialRule.value) / 100;
        pricingDetails.push({
          deliveryPricingRuleId: feriadoEspecialRule.id,
          value: feriadoEspecialAmount
        });
      }
    }
    
    // ComisionPorPago - si está seleccionada la opción productos
    if (formData.options.productos) {
      const comisionRule = this.pricingRules.find(r => r.category === Category.ComisionPorPago);
      if (comisionRule && formData.costoProductos) {
        // Calcular el monto de la comisión: porcentaje del costo de productos
        const comisionAmount = (formData.costoProductos * comisionRule.value) / 100;
        pricingDetails.push({
          deliveryPricingRuleId: comisionRule.id,
          value: comisionAmount // Monto de la comisión, no el porcentaje
        });
      }
    }
    
    // TarifaPorKmExtra - si hay kilómetros extra
    if (this.costBreakdown && this.costBreakdown.extraKmsCost > 0) {
      const extraKmRule = this.pricingRules.find(r => r.category === Category.TarifaPorKmExtra);
      if (extraKmRule) {
        pricingDetails.push({
          deliveryPricingRuleId: extraKmRule.id,
          value: this.costBreakdown.extraKmsCost // Valor real del costo por km extra calculado
        });
      }
    }
    
    // TarifaEsperaCada10Min - si está seleccionada la opción tiempo de espera (solo en modo edición)
    if (this.isEditMode && formData.options.tiempoEspera) {
      const esperaRule = this.pricingRules.find(r => r.category === Category.TarifaEsperaCada10Min);
      if (esperaRule && formData.tiempoEsperaMinutos) {
        // Calcular el costo total de espera: cantidad_demoras * tarifa_por_10_min
        // Ejemplo: si demoras = 3 y tarifa = 50, entonces costo = 3 * 50 = 150
        const totalWaitCost = formData.tiempoEsperaMinutos * esperaRule.value;
        
        pricingDetails.push({
          deliveryPricingRuleId: esperaRule.id,
          value: totalWaitCost // Costo total por tiempo de espera
        });
      }
    } else {
      
    }
    
    // RecargoFueraDeCiudad - si está seleccionada la opción recargo fuera de ciudad
    if (formData.options.recargoFueraDeCiudad) {
      const recargoFueraDeCiudadRule = this.pricingRules.find(r => r.category === Category.RecargoFueraDeCiudad);
      if (recargoFueraDeCiudadRule && this.costBreakdown) {
        // Usar el monto del desglose para no recalcular sobre una base distinta.
        const recargoAmount = this.costBreakdown.recargoFueraDeCiudadSurcharge;
        pricingDetails.push({
          deliveryPricingRuleId: recargoFueraDeCiudadRule.id,
          value: recargoAmount
        });
      }
    }
    
    // IVA - si está seleccionada la opción IVA y hay finalCost
    if (formData.options.iva && formData.finalCost) {
      const ivaRule = this.pricingRules.find(r => r.category === Category.IVA);
      if (ivaRule) {
        // Mantener el mismo IVA que se refleja en el desglose cuando está disponible.
        const finalCost = parseFloat(formData.finalCost);
        const ivaPercentage = this.getIvaPercentage();
        const ivaAmount = this.costBreakdown?.ivaAmount ?? ((finalCost * ivaPercentage) / 100);
        pricingDetails.push({
          deliveryPricingRuleId: ivaRule.id,
          value: ivaAmount
        });
      }
    }
    
    return pricingDetails;
  }

  // Verifica si los waypoints han sido modificados comparando con los originales
  waypointsWereModified(): boolean {
    if (!this.isEditMode || this.originalWaypoints.length === 0) {
      return true; // Si no es modo edición o no hay waypoints originales, generar nuevos
    }

    const formData = this.addressForm.value;
    
    // Construir waypoints actuales basados en el formulario
    const currentWaypoints: any[] = [];
    
    // 1. Agregar dirección de origen
    currentWaypoints.push({
      address: formData.pickupAddress,
      addressDetail: formData.pickupAddressDetail,
      latitude: this.pickupLat,
      longitude: this.pickupLng,
      orderIndex: 0,
    });
    
    // 2. Agregar waypoints adicionales del formulario
    this.waypointsArray.controls.forEach((control, index) => {
      const group = control as FormGroup;
      currentWaypoints.push({
        address: group.get('address')?.value,
        addressDetail: group.get('addressDetail')?.value,
        latitude: group.get('latitude')?.value,
        longitude: group.get('longitude')?.value,
        orderIndex: index + 1,
      });
    });
    
    // 3. Agregar dirección de destino
    currentWaypoints.push({
      address: formData.deliveryAddress,
      addressDetail: formData.deliveryAddressDetail,
      latitude: this.deliveryLat,
      longitude: this.deliveryLng,
      orderIndex: currentWaypoints.length,
    });

    // Ordenar waypoints originales para comparación
    const sortedOriginalWaypoints = [...this.originalWaypoints].sort(
      (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0)
    );

    // Verificar si la cantidad cambió
    if (currentWaypoints.length !== sortedOriginalWaypoints.length) {
      return true;
    }

    // Comparar cada waypoint
    for (let i = 0; i < currentWaypoints.length; i++) {
      const current = currentWaypoints[i];
      const original = sortedOriginalWaypoints[i];

      if (
        current.address !== original.address ||
        (current.addressDetail || '') !== (original.addressDetail || '') ||
        Math.abs((current.latitude || 0) - (original.latitude || 0)) > 0.000001 ||
        Math.abs((current.longitude || 0) - (original.longitude || 0)) > 0.000001
      ) {
        return true;
      }
    }

    return false; // No hubo cambios
  }

  // Genera todos los waypoints incluyendo origen, destino y waypoints adicionales
  generateAllWaypoints(): any[] {
    // En modo creación (POST): enviar todos los waypoints (origen + intermedios + destino) con orderIndex
    if (!this.isEditMode) {
      const formData = this.addressForm.value;
      const allWaypoints: any[] = [];

      // 1. Origen
      allWaypoints.push({
        address: formData.pickupAddress,
        addressDetail: formData.pickupAddressDetail || '',
        latitude: this.pickupLat ?? undefined,
        longitude: this.pickupLng ?? undefined,
        status: WaypointStatus.Pendiente,
        orderIndex: 0,
      });

      // 2. Waypoints intermedios
      this.waypointsArray.controls.forEach((control, index) => {
        const group = control as FormGroup;
        allWaypoints.push({
          address: group.get('address')?.value,
          addressDetail: group.get('addressDetail')?.value,
          latitude: group.get('latitude')?.value,
          longitude: group.get('longitude')?.value,
          status: WaypointStatus.Pendiente,
          orderIndex: index + 1,
        });
      });

      // 3. Destino
      allWaypoints.push({
        address: formData.deliveryAddress,
        addressDetail: formData.deliveryAddressDetail || '',
        latitude: this.deliveryLat ?? undefined,
        longitude: this.deliveryLng ?? undefined,
        status: WaypointStatus.Pendiente,
        orderIndex: allWaypoints.length,
      });

      return allWaypoints;
    }
    
    // En modo edición (UPDATE): Si no se modificaron los waypoints, devolver los originales con IDs
    if (!this.waypointsWereModified()) {
      return this.originalWaypoints.map(wp => ({
        id: wp.id, // Incluir ID para que el backend no los recree
        address: wp.address,
        addressDetail: wp.addressDetail,
        latitude: wp.latitude,
        longitude: wp.longitude,
        status: wp.status,
        orderIndex: wp.orderIndex,
      }));
    }
    // En modo edición (UPDATE): Si se modificaron, enviar todos los waypoints
    const formData = this.addressForm.value;
    const allWaypoints: any[] = [];
    
    // 1. Agregar dirección de origen como primer waypoint
    allWaypoints.push({
      address: formData.pickupAddress,
      addressDetail: formData.pickupAddressDetail || '',
      latitude: this.pickupLat ?? undefined,
      longitude: this.pickupLng ?? undefined,
      status: WaypointStatus.Pendiente,
      orderIndex: 0,
    });
    
    // 2. Agregar waypoints adicionales (si los hay)
    this.waypointsArray.controls.forEach((control, index) => {
      const group = control as FormGroup;
      allWaypoints.push({
        address: group.get('address')?.value,
        addressDetail: group.get('addressDetail')?.value,
        latitude: group.get('latitude')?.value,
        longitude: group.get('longitude')?.value,
        status: WaypointStatus.Pendiente,
        orderIndex: index + 1,
      });
    });
    
    // 3. Agregar dirección de destino como último waypoint
    allWaypoints.push({
      address: formData.deliveryAddress,
      addressDetail: formData.deliveryAddressDetail || '',
      latitude: this.deliveryLat ?? undefined,
      longitude: this.deliveryLng ?? undefined,
      status: WaypointStatus.Pendiente,
      orderIndex: allWaypoints.length,
    });
    
    return allWaypoints;
  }

  // Genera los pricing rule IDs basándose en los checkboxes marcados y reglas base obligatorias
  generatePricingRuleIds(): number[] {
    const formData = this.addressForm.value;
    const pricingRuleIds: number[] = [];
    
    // 1. Agregar reglas base obligatorias
    const tarifaBaseRule = this.pricingRules.find(r => r.category === Category.TarifaBase);
    if (tarifaBaseRule) {
      pricingRuleIds.push(tarifaBaseRule.id);
    }
    
    const distanciaBaseRule = this.pricingRules.find(r => r.category === Category.DistanciaBase);
    if (distanciaBaseRule) {
      pricingRuleIds.push(distanciaBaseRule.id);
    }
    
    // 2. Evaluar si se necesita TarifaPorKmExtra (si los km superan la distancia base)
    if (this.totalDistanceKm > 0 && distanciaBaseRule) {
      const baseDistance = distanciaBaseRule.value; // Distancia base en km
      if (this.totalDistanceKm > baseDistance) {
        const extraKmRule = this.pricingRules.find(r => r.category === Category.TarifaPorKmExtra);
        if (extraKmRule) {
          pricingRuleIds.push(extraKmRule.id);
        }
      }
    }
    
    // 3. Agregar reglas basándose en checkboxes marcados
    
    // DescuentoIdaVuelta - si está marcado ida y vuelta
    if (formData.options.idaVuelta) {
      const idaVueltaRule = this.pricingRules.find(r => r.category === Category.DescuentoIdaVuelta);
      if (idaVueltaRule && !pricingRuleIds.includes(idaVueltaRule.id)) {
        pricingRuleIds.push(idaVueltaRule.id);
      }
    }
    
    // CargoSobredimension - si está marcado objeto fuera de caja
    if (formData.options.objetoFueraCaja) {
      const sobredimensionRule = this.pricingRules.find(r => r.category === Category.CargoSobredimension);
      if (sobredimensionRule && !pricingRuleIds.includes(sobredimensionRule.id)) {
        pricingRuleIds.push(sobredimensionRule.id);
      }
    }
    
    // RecargoLluviaBase y RecargoLluviaPorKm - si está marcado lluvia (se mandan las 2 reglas)
    if (formData.options.lluvia) {
      const lluviaBaseRule = this.pricingRules.find(r => r.category === Category.RecargoLluviaBase);
      if (lluviaBaseRule && !pricingRuleIds.includes(lluviaBaseRule.id)) {
        pricingRuleIds.push(lluviaBaseRule.id);
      }
      
      const lluviaPorKmRule = this.pricingRules.find(r => r.category === Category.RecargoLluviaPorKm);
      if (lluviaPorKmRule && !pricingRuleIds.includes(lluviaPorKmRule.id)) {
        pricingRuleIds.push(lluviaPorKmRule.id);
      }
    }
    
    // ComisionPorPago - si está marcado productos
    if (formData.options.productos) {
      const comisionRule = this.pricingRules.find(r => r.category === Category.ComisionPorPago);
      if (comisionRule && !pricingRuleIds.includes(comisionRule.id)) {
        pricingRuleIds.push(comisionRule.id);
      }
    }
    
    // TarifaEsperaCada10Min - si está marcado tiempo de espera (solo en modo edición)
    if (this.isEditMode && formData.options.tiempoEspera) {
      const esperaRule = this.pricingRules.find(r => r.category === Category.TarifaEsperaCada10Min);
      if (esperaRule && !pricingRuleIds.includes(esperaRule.id)) {
        pricingRuleIds.push(esperaRule.id);
      }
    }
    
    // FeriadoComun - si está marcado feriado común
    if (formData.options.feriadoComun) {
      const feriadoComunRule = this.pricingRules.find(r => r.category === Category.FeriadoComun);
      if (feriadoComunRule && !pricingRuleIds.includes(feriadoComunRule.id)) {
        pricingRuleIds.push(feriadoComunRule.id);
      }
    }
    
    // FeriadoEspecial - si está marcado feriado especial
    if (formData.options.feriadoEspecial) {
      const feriadoEspecialRule = this.pricingRules.find(r => r.category === Category.FeriadoEspecial);
      if (feriadoEspecialRule && !pricingRuleIds.includes(feriadoEspecialRule.id)) {
        pricingRuleIds.push(feriadoEspecialRule.id);
      }
    }
    
    // RecargoFueraDeCiudad - si está marcado recargo fuera de ciudad
    if (formData.options.recargoFueraDeCiudad) {
      const recargoFueraDeCiudadRule = this.pricingRules.find(r => r.category === Category.RecargoFueraDeCiudad);
      if (recargoFueraDeCiudadRule && !pricingRuleIds.includes(recargoFueraDeCiudadRule.id)) {
        pricingRuleIds.push(recargoFueraDeCiudadRule.id);
      }
    }
    
    // IVA - si está marcado IVA
    if (formData.options.iva) {
      const ivaRule = this.pricingRules.find(r => r.category === Category.IVA);
      if (ivaRule && !pricingRuleIds.includes(ivaRule.id)) {
        pricingRuleIds.push(ivaRule.id);
      }
    }
    
    return pricingRuleIds;
  }

  onSubmit() {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    this.loading = true;

    const formData = this.addressForm.value;
    const options = formData.options;
      
    // Generar detalles de pricing
    const pricingDetails = this.generatePricingDetails();

    if (this.isEditMode && this.orderId) {
      // Calculate finalCostWithIVA if finalCost exists AND IVA checkbox is checked
      let finalCostWithIVA: number | undefined = undefined;
      const ivaChecked = formData.options?.iva;
      if (formData.finalCost && ivaChecked) {
        const finalCost = parseFloat(formData.finalCost);
        const ivaPercentage = this.getIvaPercentage();
        if (ivaPercentage > 0) {
          const ivaAmount = finalCost * (ivaPercentage / 100);
          finalCostWithIVA = finalCost + ivaAmount;
        } else {
          finalCostWithIVA = finalCost;
        }
      }

      // Update existing order
      const updateOrderDto: UpdateParticularOrderDto = {
        id: this.orderId,
        cdClient: this.selectedClientId ?? undefined,
        originAddress: formData.pickupAddress,
        originLatitude: this.pickupLat ?? undefined,
        originLongitude: this.pickupLng ?? undefined,
        destinationAddress: formData.deliveryAddress,
        destinationLatitude: this.deliveryLat ?? undefined,
        destinationLongitude: this.deliveryLng ?? undefined,
        recipientName: formData.personName,
        pickupTimeRange: formData.pickupTimeRange,
        deliveryTimeRange: formData.deliveryTimeRange,
        pickupPhone: formData.pickupPhone,
        deliveryPhone: formData.deliveryPhone,
        observations: formData.observations,
        whereToPay: this.getPaymentLocationValue(formData.paymentLocation),
        distanceKm: this.totalDistanceKm,
        calculatedCost: parseFloat(formData.costo),
        finalCost: formData.finalCost ? parseFloat(formData.finalCost) : undefined,
        finalCostWithIVA: finalCostWithIVA,
        status: this.orderStatus ?? ParticularOrderStatus.Nuevo,
        waypoints: this.generateAllWaypoints(),
        pricingRuleIds: this.generatePricingRuleIds(),
        particularOrderPricingDetail: pricingDetails,
        scheduledAt: formData.options.isScheduled && formData.options.scheduledAt ? formData.options.scheduledAt.toISOString() : undefined
      };
      
      console.log('🚀 UPDATE DTO being sent:', updateOrderDto);
      console.log('🚀 UPDATE DTO waypoints:', updateOrderDto.waypoints);
      console.log('🚀 UPDATE DTO waypoints with IDs:', updateOrderDto.waypoints?.filter(w => w.id));
      console.log('🚀 UPDATE DTO finalCost field:', updateOrderDto.finalCost);

      this.particularOrderService
        .updateParticularOrder(this.orderId, updateOrderDto)
        .subscribe({
          next: (updatedOrder) => {
            this.loading = false;
            this.router.navigate(['/particular-orders-list']);
          },
          error: (error) => {
            this.loading = false;
            console.error('🚨 Error updating order:', error);
            console.error('🚨 Error status:', error.status);
            console.error('🚨 Error message:', error.message);
            console.error('🚨 Error details:', error.error);
            this.showErrorDialog(
              'Error',
              'No se pudo actualizar el pedido. Por favor, intente nuevamente.'
            );
          },
        });
    } else {
      // Calculate finalCostWithIVA if finalCost exists AND IVA checkbox is checked
      let finalCostWithIVA: number | undefined = undefined;
      const ivaChecked = formData.options?.iva;
      if (formData.finalCost && ivaChecked) {
        const finalCost = parseFloat(formData.finalCost);
        const ivaPercentage = this.getIvaPercentage();
        if (ivaPercentage > 0) {
          const ivaAmount = finalCost * (ivaPercentage / 100);
          finalCostWithIVA = finalCost + ivaAmount;
        } else {
          finalCostWithIVA = finalCost;
        }
      }

      // Create new order
      const createOrderDto: CreateParticularOrderDto = {
        cdClient: this.selectedClientId ?? undefined,
        recipientName: formData.personName,
        pickupTimeRange: formData.pickupTimeRange,
        deliveryTimeRange: formData.deliveryTimeRange,
        pickupPhone: formData.pickupPhone,
        deliveryPhone: formData.deliveryPhone,
        observations: formData.observations,
        whereToPay: this.getPaymentLocationValue(formData.paymentLocation),
        distanceKm: this.totalDistanceKm,
        calculatedCost: parseFloat(formData.costo),
        finalCost: formData.finalCost ? parseFloat(formData.finalCost) : undefined,
        finalCostWithIVA: finalCostWithIVA,
        status: ParticularOrderStatus.Nuevo,
        waypoints: this.generateAllWaypoints(),
        pricingRuleIds: this.generatePricingRuleIds(),
        particularOrderPricingDetail: pricingDetails,
        scheduledAt: formData.options.isScheduled && formData.options.scheduledAt ? formData.options.scheduledAt.toISOString() : undefined
      };
      
      console.log('🚀 CREATE DTO being sent:', createOrderDto);
      console.log('🚀 CREATE DTO waypoints:', createOrderDto.waypoints);
      console.log('🚀 CREATE DTO finalCost field:', createOrderDto.finalCost);
      
      this.particularOrderService.createParticularOrder(createOrderDto).subscribe({
        next: (createdOrder) => {
          this.loading = false;
          this.router.navigate(['/particular-orders-list']);
        },
        error: (error) => {
          this.loading = false;
          console.error('🚨 Error creating order:', error);
          console.error('🚨 Error status:', error.status);
          console.error('🚨 Error message:', error.message);
          console.error('🚨 Error details:', error.error);
          this.showErrorDialog(
            'Error',
            'No se pudo crear el pedido. Por favor, intente nuevamente.'
          );
        },
      });
    }
  }

  private getIvaPercentage(): number {
    if (this.ivaPercentage !== null && this.ivaPercentage !== undefined) {
      return this.ivaPercentage;
    }

    const ivaRule = this.pricingRules.find(rule => rule.category === Category.IVA);
    return ivaRule?.value ?? 0;
  }

  onInputChange() {
    this.addressChange.emit({
      pickupAddress: this.addressForm.get('pickupAddress')?.value,
      deliveryAddress: this.addressForm.get('deliveryAddress')?.value,
    });
  }

  onCancelShipment(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Cancelar Envío',
        message: '¿Confirmás la cancelación del pedido?\nEsta acción cancelará todo el envío y no se podrá deshacer.',
        showCancel: true,
        confirmText: 'Sí',
        cancelText: 'No'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.orderId) {
        console.log('Cancelando envío...');
        this.finishOrCancelShipment(this.orderId, false); // false = cancel
      }
    });
  }

  onFinalizeShipment(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Finalizar Envío',
        message: 'Estás por marcar el envío como finalizado.\n¿Querés continuar?',
        showCancel: true,
        confirmText: 'Sí',
        cancelText: 'No'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.orderId) {
        console.log('Finalizando envío...');
        this.finishOrCancelShipment(this.orderId, true); // true = finish
      }
    });
  }

  private finishOrCancelShipment(orderId: number, isFinish: boolean): void {
    const url = `${environment.apiBaseUrl}/api/ParticularOrder/FinishOrCancelShipment`;
    
    const requestBody = {
      orderId: orderId,
      isFinish: isFinish,
      userId: 0 // Default user ID as shown in the API example
    };
    
    this.http.put<any>(url, requestBody).subscribe({
      next: (response) => {
        console.log('Operación exitosa:', response);
        const action = isFinish ? 'finalizado' : 'cancelado';
        
        // Mostrar mensaje de éxito
        this.dialog.open(ConfirmDialogComponent, {
          width: '400px',
          data: {
            title: 'Operación Exitosa',
            message: `El envío ha sido ${action} correctamente.`,
            showCancel: false,
            confirmText: 'Aceptar'
          }
        });
        
        // Redirigir a la lista después de un breve delay
        setTimeout(() => {
          this.router.navigate(['/particular-orders-list']);
        }, 1500);
      },
      error: (error) => {
        console.error('🚨 Error en la operación:', error);
        console.error('🚨 Error status:', error.status);
        console.error('🚨 Error message:', error.message);
        console.error('🚨 Error details:', error.error);
        const action = isFinish ? 'finalizar' : 'cancelar';
        
        this.dialog.open(ConfirmDialogComponent, {
          width: '400px',
          data: {
            title: 'Error',
            message: `Ocurrió un error al ${action} el envío. Por favor, inténtalo nuevamente.`,
            showCancel: false,
            confirmText: 'Aceptar'
          }
        });
      }
    });
  }
}
